import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { useQuote, useUpdateQuoteStatus, useConvertQuote, useDuplicateQuote, useUpdateQuote, useClients, useSendQuoteEmail, useUpdateClient, downloadQuotePdf, openQuotePdfForPrint, useUploadQuoteImages, useQuoteImages, useDeleteQuoteImage } from '@/hooks/useApi';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import PageSkeleton from '@/components/PageSkeleton';
import { useQueryClient } from '@tanstack/react-query';
import {
  PencilSquareIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathRoundedSquareIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  PrinterIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentCheckIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'es', label: 'Español' },
];

const STATUS_COLORS: Record<string, string> = {
  draft: 'badge-gray', sent: 'badge-blue', viewed: 'badge-blue',
  accepted: 'badge-green', rejected: 'badge-red', expired: 'badge-gray', converted: 'badge-green',
};

const TRANSITIONS: Record<string, string[]> = {
  draft: ['sent'],
  sent: ['accepted', 'rejected'],
  viewed: ['accepted', 'rejected'],
  accepted: ['converted'],
};

const VAT_RATES = ['21', '12', '6', '0'];

interface LineItem {
  description: string;
  quantity: number;
  unitPrice: string;
  vatRate: string;
}

export default function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: quote, isLoading, isError, error } = useQuote(id!);
  const updateStatus = useUpdateQuoteStatus();
  const convertQuote = useConvertQuote();
  const duplicateQuote = useDuplicateQuote();
  const updateQuote = useUpdateQuote();
  const sendQuoteEmail = useSendQuoteEmail();
  const updateClient = useUpdateClient();
  const qc = useQueryClient();

  const [editing, setEditing] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendLanguage, setSendLanguage] = useState('');
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);
  const [confirmMarkSent, setConfirmMarkSent] = useState(false);
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [clientId, setClientId] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  const [title, setTitle] = useState('');
  const [lines, setLines] = useState<LineItem[]>([]);
  
  // Image state
  const [newImages, setNewImages] = useState<File[]>([]);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const uploadImages = useUploadQuoteImages();
  const { data: existingImages = [] } = useQuoteImages(id || '');
  const deleteImage = useDeleteQuoteImage();

  const { data: clientsData } = useClients(editing ? {} : { enabled: false });

  useEffect(() => {
    if (!quote) return;

    const isEditMode = location.pathname.includes('/edit');
    const isCopyMode = location.pathname.includes('/copy');

    if ((isEditMode && quote.status === 'draft') || isCopyMode) {
      setEditing(true);
      return;
    }

    setEditing(false);
  }, [location.pathname, quote]);

  // Populate form state when entering edit mode
  useEffect(() => {
    if (editing && quote) {
      setClientId(quote.client?.id || '');
      setIssueDate(quote.issueDate || '');
      setValidUntil(quote.validUntil || '');
      setNotes(quote.notes || '');
      setTitle(quote.title || '');
      setLines(
        quote.lines?.map((l) => ({
          description: l.description,
          quantity: Number(l.quantity),
          unitPrice: String(l.unitPrice),
          vatRate: String(Number(l.vatRate)),
        })) || [{ description: '', quantity: 1, unitPrice: '', vatRate: '21' }]
      );
    }
  }, [editing, quote]);

  if (isLoading) return <PageSkeleton variant="detail" />;

  if (isError) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">{status === 404 ? t('common.notFound') : t('common.error')}</p>
        <button onClick={() => navigate('/quotes')} className="mt-4 text-sm text-blue-500 hover:underline">{t('common.back')}</button>
      </div>
    );
  }

  if (!quote) return <p className="text-gray-400 py-12 text-center">{t('common.notFound')}</p>;

  const isDraft = quote.status === 'draft';
  const transitions = TRANSITIONS[quote.status] || [];

  function handleStatusChange(status: string) {
    updateStatus.mutate(
      { id: quote!.id, status },
      {
        onSuccess: () => toast.success(t('common.saved')),
        onError: () => toast.error(t('common.error')),
      }
    );
  }

  function handleConvert() {
    convertQuote.mutate(quote!.id, {
      onSuccess: (data: { invoiceId: string }) => {
        navigate(`/invoices/${data.invoiceId}`);
      },
    });
  }

  function handleDuplicate() {
    duplicateQuote.mutate(quote!.id, {
      onSuccess: (data: { id: string }) => {
        setConfirmDuplicate(false);
        navigate(`/quotes/${data.id}/copy`);
      },
    });
  }

  function handleMarkAsSent() {
    updateStatus.mutate(
      { id: quote!.id, status: 'sent' },
      {
        onSuccess: () => {
          setConfirmMarkSent(false);
          toast.success(t('common.saved'));
        },
        onError: () => {
          setConfirmMarkSent(false);
          toast.error(t('common.error'));
        },
      },
    );
  }

  async function handlePdfPreview() {
    setPdfLoading(true);
    setShowPdfPreview(true);
    try {
      const { data } = await api.get(`/quotes/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      setPdfUrl(url);
    } catch {
      toast.error(t('quotes.pdfPreviewError'));
      setShowPdfPreview(false);
    } finally {
      setPdfLoading(false);
    }
  }

  async function handlePdfDownload() {
    try {
      await downloadQuotePdf(quote!.id, `${quote!.quoteNumber || 'quote'}.pdf`);
    } catch {
      toast.error(t('common.error'));
    }
  }

  async function handlePdfPrint() {
    try {
      await openQuotePdfForPrint(quote!.id);
    } catch {
      toast.error(t('common.error'));
    }
  }

  function closePdfPreview() {
    setShowPdfPreview(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  }

  function handleSendEmail() {
    if (!quote) return;
    const trimmedName = recipientName.trim();
    const trimmedEmail = recipientEmail.trim();

    if (!trimmedEmail) {
      toast.error(t('quotes.send.emailRequired'));
      return;
    }

    const currentName = quote.client?.name || '';
    const currentEmail = quote.client?.email || '';
    const clientChanged =
      (trimmedName && trimmedName !== currentName) ||
      trimmedEmail !== currentEmail;

    const send = () =>
      sendQuoteEmail.mutate(
        { id: quote.id, language: sendLanguage },
        {
          onSuccess: () => {
            setShowSendModal(false);
          },
          onError: () => {
            toast.error(t('quotes.sendFailed'));
          },
        },
      );

    if (clientChanged && quote.client?.id) {
      updateClient.mutate(
        {
          id: quote.client.id,
          name: trimmedName || currentName,
          email: trimmedEmail,
        },
        {
          onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['quote', quote.id] });
            send();
          },
          onError: () => {
            toast.error(t('quotes.send.clientUpdateFailed'));
          },
        },
      );
      return;
    }

    send();
  }

  function openSendModal() {
    if (!quote) return;
    setSendLanguage(quote.client?.language || i18n.language?.split('-')[0] || 'en');
    setRecipientName(quote.client?.name || '');
    setRecipientEmail(quote.client?.email || '');
    setShowSendModal(true);
  }

  // ── Edit helpers ──
  function addLine() {
    setLines([...lines, { description: '', quantity: 1, unitPrice: '1', vatRate: '21' }]);
  }

  function removeLine(idx: number) {
    if (lines.length <= 1) return;
    setLines(lines.filter((_, i) => i !== idx));
  }

  function updateLine(idx: number, field: keyof LineItem, value: string | number) {
    setLines(lines.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  }

  function lineTotal(l: LineItem) {
    const net = l.quantity * Number(l.unitPrice || 0);
    return { net, vat: net * (Number(l.vatRate) / 100), gross: net * (1 + Number(l.vatRate) / 100) };
  }

  const editTotals = lines.reduce(
    (a, l) => { const lt = lineTotal(l); return { net: a.net + lt.net, vat: a.vat + lt.vat, gross: a.gross + lt.gross }; },
    { net: 0, vat: 0, gross: 0 }
  );

  // Image handlers
  function handleImageSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    setNewImages((prev) => [...prev, ...files]);
  }

  function removeNewImage(idx: number) {
    setNewImages((prev) => prev.filter((_, i) => i !== idx));
  }

  function removeExistingImage(filename: string) {
    if (!window.confirm(t('quotes.confirmDeleteImage', 'Are you sure you want to delete this image?'))) {
      return;
    }
    deleteImage.mutate(
      { quoteId: id!, filename },
      {
        onSuccess: () => {
          toast.success(t('quotes.imageDeleted', 'Image deleted'));
        },
        onError: () => {
          toast.error(t('quotes.imageDeleteFailed', 'Failed to delete image'));
        },
      }
    );
  }

  function handleSave(e: React.FormEvent) {
    e.preventDefault();
    updateQuote.mutate(
      {
        id: quote!.id,
        clientId,
        issueDate,
        validUntil,
        notes,
        title,
        lines: lines.map((l) => ({
          description: l.description,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          vatRate: l.vatRate,
        })),
      },
      {
        onSuccess: () => {
          // Upload new images if any
          if (newImages.length > 0) {
            uploadImages.mutate(
              { quoteId: quote!.id, files: newImages },
              {
                onSuccess: () => {
                  toast.success(t('quotes.saved'));
                  setNewImages([]);
                  qc.invalidateQueries({ queryKey: ['quote-images', quote!.id] });
                  navigate(`/quotes/${quote!.id}`, { replace: true });
                },
                onError: () => {
                  toast.error(t('quotes.imagesUploadFailed', 'Failed to upload images'));
                  navigate(`/quotes/${quote!.id}`, { replace: true });
                },
              }
            );
          } else {
            toast.success(t('quotes.saved'));
            navigate(`/quotes/${quote!.id}`, { replace: true });
          }
        },
      }
    );
  }

  // ═══════════════════════════════════════════════
  // EDIT MODE
  // ═══════════════════════════════════════════════
  if (editing) {
    return (
      <div>
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(`/quotes/${quote.id}`)} className="text-gray-400 hover:text-gray-600">←</button>
          <h1 className="text-2xl font-bold">{t('quotes.editQuote', 'Edit Quote')}</h1>
          <span className="badge badge-gray">{quote.quoteNumber}</span>
        </div>

        <form onSubmit={handleSave}>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3 space-y-6">
              {/* Header fields */}
              <div className="card card-body grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="label">{t('quotes.client')} *</label>
                  <select className="input" value={clientId} onChange={(e) => setClientId(e.target.value)} required>
                    <option value="">{t('invoices.selectClient')}</option>
                    {clientsData?.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">{t('quotes.date')} *</label>
                  <input type="date" className="input" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} required />
                </div>
                <div>
                  <label className="label">{t('quotes.validUntil')} *</label>
                  <input type="date" className="input" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} required />
                </div>
                <div>
                  <label className="label">{t('quotes.title', 'Title')}</label>
                  <input type="text" className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
                </div>
              </div>

              {/* Lines */}
              <div className="card">
                <div className="card-header flex items-center justify-between">
                  <h3 className="font-semibold">{t('invoices.lines')}</h3>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={addLine}>+ {t('common.add')}</button>
                </div>
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>{t('invoices.description')}</th>
                        <th className="w-20">{t('invoices.quantity')}</th>
                        <th className="w-36">{t('invoices.unitPrice')}</th>
                        <th className="w-28">{t('invoices.vat')}</th>
                        <th className="text-right w-28">{t('common.amount')}</th>
                        <th className="w-10" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {lines.map((line, i) => {
                        const lt = lineTotal(line);
                        return (
                          <tr key={i}>
                            <td><input type="text" className="input" value={line.description} onChange={(e) => updateLine(i, 'description', e.target.value)} required /></td>
                            <td><input type="number" min={1} className="input text-center" value={line.quantity} onChange={(e) => updateLine(i, 'quantity', Number(e.target.value))} required /></td>
                            <td><input type="number" step="0.01" className="input text-right" value={line.unitPrice} onChange={(e) => updateLine(i, 'unitPrice', e.target.value)} required /></td>
                            <td>
                              <select className="input min-w-[5rem]" value={line.vatRate} onChange={(e) => updateLine(i, 'vatRate', e.target.value)}>
                                {VAT_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
                              </select>
                            </td>
                            <td className="text-right font-medium">€{lt.gross.toFixed(2)}</td>
                            <td><button type="button" className="text-red-400 hover:text-red-600" onClick={() => removeLine(i)} disabled={lines.length <= 1}>✕</button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Notes */}
              <div className="card card-body">
                <label className="label">{t('common.notes')}</label>
                <textarea className="input min-h-[80px]" value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>

              {/* Images */}
              <div className="card">
                <div className="card-header flex items-center justify-between">
                  <h3 className="font-semibold text-base text-gray-900">{t('quotes.images', 'Images')}</h3>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => imageInputRef.current?.click()}
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t('quotes.addImages', 'Add Images')}
                  </button>
                  <input
                    ref={imageInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                </div>
                
                <div className="card-body space-y-4">
                  {/* Existing images */}
                  {existingImages.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">{t('quotes.existingImages', 'Existing Images')}</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {existingImages.map((img: any, idx: number) => (
                          <div key={idx} className="relative group">
                            <img
                              src={img.url}
                              alt={`Existing ${idx + 1}`}
                              className="w-full h-32 object-cover rounded-lg border border-gray-200"
                            />
                            <button
                              type="button"
                              onClick={() => removeExistingImage(img.filename)}
                              className="absolute top-1 right-1 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* New images */}
                  {newImages.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">{t('quotes.newImages', 'New Images')}</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {newImages.map((file, idx) => (
                          <div key={idx} className="relative group">
                            <img
                              src={URL.createObjectURL(file)}
                              alt={`New ${idx + 1}`}
                              className="w-full h-32 object-cover rounded-lg border border-gray-200"
                            />
                            <button
                              type="button"
                              onClick={() => removeNewImage(idx)}
                              className="absolute top-1 right-1 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                            <span className="absolute bottom-1 left-1 px-2 py-0.5 bg-black/70 text-white text-xs rounded">
                              {file.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {existingImages.length === 0 && newImages.length === 0 && (
                    <div 
                      className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-gray-400 transition-colors"
                      onClick={() => imageInputRef.current?.click()}
                    >
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="mt-2 text-sm text-gray-600">{t('quotes.dropImages', 'Click to upload images')}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar totals */}
            <div>
              <div className="card card-body space-y-3 sidebar-sticky">
                <div className="flex justify-between text-sm"><span className="text-gray-500">{t('invoices.subtotal')}</span><span>€{editTotals.net.toFixed(2)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">{t('invoices.vat')}</span><span>€{editTotals.vat.toFixed(2)}</span></div>
                <hr />
                <div className="flex justify-between text-lg"><span className="font-bold">{t('invoices.total')}</span><span className="font-bold">€{editTotals.gross.toFixed(2)}</span></div>
                <button type="submit" className="btn btn-primary w-full mt-4" disabled={updateQuote.isPending}>
                  {updateQuote.isPending ? t('common.saving') : t('common.save')}
                </button>
                <button type="button" className="btn btn-secondary w-full" onClick={() => navigate(`/quotes/${quote.id}`)}>
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    );
  }

  // ═══════════════════════════════════════════════
  // VIEW MODE
  // ═══════════════════════════════════════════════
  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-start sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/quotes')} className="text-gray-400 hover:text-gray-600">←</button>
          <div>
            <h1 className="text-2xl font-bold">{quote.quoteNumber || `${t('quotes.draftQuote')}${quote.client?.name ? ` ${t('common.of')} ${quote.client.name}` : ''}`}</h1>
            {quote.title && <p className="text-gray-500">{quote.title}</p>}
          </div>
          <span className={`badge ${STATUS_COLORS[quote.status] || 'badge-gray'} ml-2`}>
            {t(`quotes.status.${quote.status}`)}
          </span>
        </div>
      </div>

      {/* PDF Preview Modal */}
      {showPdfPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl mx-4 h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
                {t('quotes.pdfPreview')} — {quote.quoteNumber}
              </h3>
              <button onClick={closePdfPreview} className="text-gray-400 hover:text-gray-600 p-1">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 bg-gray-100">
              {pdfLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
                </div>
              ) : pdfUrl ? (
                <iframe
                  src={pdfUrl}
                  className="w-full h-full border-0"
                  title="PDF Preview"
                />
              ) : null}
            </div>
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
              <button className="btn btn-secondary" onClick={handlePdfDownload}>
                {t('quotes.actions.download')}
              </button>
              <button className="btn btn-secondary" onClick={handlePdfPrint}>
                {t('quotes.actions.print')}
              </button>
              <button className="btn btn-secondary" onClick={closePdfPreview}>
                {t('common.close')}
              </button>
              {(quote.status === 'draft' || quote.status === 'sent') && (
                <button
                  className="btn bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => {
                    closePdfPreview();
                    openSendModal();
                  }}
                >
                  <svg className="w-4 h-4 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {t('quotes.actions.sendToClient')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirm: Mark as sent */}
      {confirmMarkSent && (
        <ConfirmModal
          tone="amber"
          icon={<ExclamationTriangleIcon className="w-6 h-6 text-amber-600" />}
          title={t('quotes.confirmMarkSent.title')}
          description={t('quotes.confirmMarkSent.description')}
          confirmLabel={t('quotes.actions.markAsSent')}
          confirmTone="amber"
          pending={updateStatus.isPending}
          onCancel={() => setConfirmMarkSent(false)}
          onConfirm={handleMarkAsSent}
        />
      )}

      {/* Confirm: Duplicate */}
      {confirmDuplicate && (
        <ConfirmModal
          tone="blue"
          icon={<DocumentDuplicateIcon className="w-6 h-6 text-blue-600" />}
          title={t('quotes.confirmDuplicate.title')}
          description={t('quotes.confirmDuplicate.description')}
          confirmLabel={t('quotes.actions.duplicate')}
          confirmTone="blue"
          pending={duplicateQuote.isPending}
          onCancel={() => setConfirmDuplicate(false)}
          onConfirm={handleDuplicate}
        />
      )}

      {/* Send Email Modal */}
      {showSendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {t('quotes.sendQuoteTitle')}
              </h3>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-3">{t('quotes.sendQuoteDescription')}</p>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {t('quotes.send.recipient')}
                  </p>
                  <div>
                    <label className="label">{t('quotes.client')}</label>
                    <input
                      type="text"
                      className="input"
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="label">{t('quotes.send.email')}</label>
                    <input
                      type="email"
                      className="input"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      placeholder="client@example.com"
                      required
                    />
                    <p className="text-xs text-gray-400 mt-1">{t('quotes.send.editHint')}</p>
                  </div>
                </div>
              </div>
              <div>
                <label className="label">{t('quotes.emailLanguage')}</label>
                <select className="input" value={sendLanguage} onChange={(e) => setSendLanguage(e.target.value)}>
                  {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                </select>
                <p className="text-xs text-gray-400 mt-1">{t('quotes.emailLanguageHint')}</p>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-2 border-t">
              <button className="btn btn-secondary" onClick={() => setShowSendModal(false)}>
                {t('common.cancel')}
              </button>
              <button
                className="btn bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={handleSendEmail}
                disabled={sendQuoteEmail.isPending || updateClient.isPending}
              >
                {sendQuoteEmail.isPending || updateClient.isPending ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                    {t('quotes.sending')}
                  </span>
                ) : (
                  t('quotes.actions.sendToClient')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Lines */}
        <div className="lg:col-span-3 space-y-6">
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold">{t('invoices.lines')}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th className="w-2/5">{t('invoices.description')}</th>
                    <th className="text-center">{t('invoices.quantity')}</th>
                    <th className="text-right">{t('invoices.unitPrice')}</th>
                    <th className="text-center">{t('invoices.vat')}</th>
                    <th className="text-right">{t('common.amount')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {quote.lines?.map((line) => (
                    <tr key={line.id}>
                      <td>{line.description}</td>
                      <td className="text-center">{Number(line.quantity)}</td>
                      <td className="text-right">€{Number(line.unitPrice).toFixed(2)}</td>
                      <td className="text-center">{Number(line.vatRate)}%</td>
                      <td className="text-right font-medium">€{Number(line.lineTotal).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {quote.notes && (
            <div className="card card-body">
              <h3 className="font-semibold mb-2">{t('common.notes')}</h3>
              <p className="text-gray-600 whitespace-pre-wrap">{quote.notes}</p>
            </div>
          )}

          {quote.termsAndConditions && (
            <div className="card card-body">
              <h3 className="font-semibold mb-2">{t('quotes.termsAndConditions')}</h3>
              <p className="text-gray-600 whitespace-pre-wrap">{quote.termsAndConditions}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick actions */}
          <div className="card card-body space-y-1">
            <h3 className="font-semibold mb-2">{t('common.actions')}</h3>

            {isDraft && (
              <ActionButton
                icon={<PencilSquareIcon className="w-4 h-4" />}
                label={t('common.edit')}
                onClick={() => navigate(`/quotes/${quote.id}/edit`)}
              />
            )}

            {transitions.includes('sent') && (
              <ActionButton
                icon={<ClipboardDocumentCheckIcon className="w-4 h-4" />}
                label={t('quotes.actions.markAsSent')}
                onClick={() => setConfirmMarkSent(true)}
                disabled={updateStatus.isPending}
              />
            )}

            {(quote.status === 'draft' || quote.status === 'sent') && (
              <ActionButton
                icon={<PaperAirplaneIcon className="w-4 h-4" />}
                label={t('quotes.actions.sendToClient')}
                tone="emerald"
                onClick={openSendModal}
              />
            )}

            {transitions.includes('accepted') && (
              <ActionButton
                icon={<CheckCircleIcon className="w-4 h-4" />}
                label={t('quotes.status.accepted')}
                tone="green"
                onClick={() => handleStatusChange('accepted')}
                disabled={updateStatus.isPending}
              />
            )}

            {transitions.includes('rejected') && (
              <ActionButton
                icon={<XCircleIcon className="w-4 h-4" />}
                label={t('quotes.status.rejected')}
                tone="red"
                onClick={() => handleStatusChange('rejected')}
                disabled={updateStatus.isPending}
              />
            )}

            {quote.status === 'accepted' && (
              <ActionButton
                icon={<ArrowPathRoundedSquareIcon className="w-4 h-4" />}
                label={t('quotes.actions.convertToInvoice')}
                tone="green"
                onClick={handleConvert}
                disabled={convertQuote.isPending}
              />
            )}

            <div className="border-t border-gray-100 my-2" />

            <ActionButton
              icon={<EyeIcon className="w-4 h-4" />}
              label={t('quotes.actions.previewPdf')}
              onClick={handlePdfPreview}
              disabled={pdfLoading}
            />
            <ActionButton
              icon={<ArrowDownTrayIcon className="w-4 h-4" />}
              label={t('quotes.actions.download')}
              onClick={handlePdfDownload}
            />
            <ActionButton
              icon={<PrinterIcon className="w-4 h-4" />}
              label={t('quotes.actions.print')}
              onClick={handlePdfPrint}
            />
            <ActionButton
              icon={<DocumentDuplicateIcon className="w-4 h-4" />}
              label={t('quotes.actions.duplicate')}
              onClick={() => setConfirmDuplicate(true)}
              disabled={duplicateQuote.isPending}
            />
          </div>

          <div className="card card-body space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('invoices.subtotal')}</span>
              <span>€{Number(quote.subtotal).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('invoices.vat')}</span>
              <span>€{Number(quote.vatAmount).toFixed(2)}</span>
            </div>
            <hr />
            <div className="flex justify-between text-lg">
              <span className="font-bold">{t('invoices.total')}</span>
              <span className="font-bold">€{Number(quote.totalAmount).toFixed(2)}</span>
            </div>
          </div>

          <div className="card card-body space-y-3">
            <h3 className="font-semibold">{t('common.details')}</h3>
            <div className="text-sm space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-500">{t('quotes.client')}</span>
                <span className="font-medium">{quote.client?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('quotes.date')}</span>
                <span>{new Date(quote.issueDate).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('quotes.validUntil')}</span>
                <span>{quote.validUntil ? new Date(quote.validUntil).toLocaleDateString() : '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('common.currency')}</span>
                <span>{quote.currency}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('common.createdAt')}</span>
                <span>{new Date(quote.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {/* Client comments */}
          {quote.comments && quote.comments.length > 0 && (
            <div className="card card-body space-y-3">
              <h3 className="font-semibold">{t('quotes.comments.title')}</h3>
              <div className="space-y-3">
                {quote.comments.map((c) => (
                  <div key={c.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-900">{c.authorName}</span>
                      <span className="text-xs text-gray-400">{new Date(c.createdAt).toLocaleString()}</span>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{c.message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Local presentational helpers
// ─────────────────────────────────────────────────────────────

type ActionTone = 'default' | 'emerald' | 'green' | 'red' | 'amber' | 'blue';

const TONE_CLASSES: Record<ActionTone, string> = {
  default: 'text-gray-700 hover:bg-gray-50',
  emerald: 'text-emerald-700 hover:bg-emerald-50',
  green: 'text-green-700 hover:bg-green-50',
  red: 'text-red-700 hover:bg-red-50',
  amber: 'text-amber-700 hover:bg-amber-50',
  blue: 'text-blue-700 hover:bg-blue-50',
};

function ActionButton({
  icon,
  label,
  onClick,
  disabled,
  tone = 'default',
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: ActionTone;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${TONE_CLASSES[tone]}`}
    >
      <span className="shrink-0">{icon}</span>
      <span className="text-left">{label}</span>
    </button>
  );
}

const CONFIRM_BTN_CLASSES: Record<ActionTone, string> = {
  default: 'bg-gray-700 hover:bg-gray-800 text-white',
  emerald: 'bg-emerald-600 hover:bg-emerald-700 text-white',
  green: 'bg-green-600 hover:bg-green-700 text-white',
  red: 'bg-red-600 hover:bg-red-700 text-white',
  amber: 'bg-amber-600 hover:bg-amber-700 text-white',
  blue: 'bg-blue-600 hover:bg-blue-700 text-white',
};

function ConfirmModal({
  icon,
  title,
  description,
  confirmLabel,
  confirmTone = 'default',
  pending,
  onCancel,
  onConfirm,
}: {
  tone?: ActionTone;
  icon: React.ReactNode;
  title: string;
  description: string;
  confirmLabel: string;
  confirmTone?: ActionTone;
  pending?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="px-6 pt-6 pb-4 flex items-start gap-3">
          <div className="shrink-0">{icon}</div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-600 mt-1">{description}</p>
          </div>
        </div>
        <div className="px-6 py-4 bg-gray-50 flex justify-end gap-2 border-t">
          <button className="btn btn-secondary" onClick={onCancel} disabled={pending}>
            {t('common.cancel')}
          </button>
          <button
            className={`btn ${CONFIRM_BTN_CLASSES[confirmTone]}`}
            onClick={onConfirm}
            disabled={pending}
          >
            {pending ? '…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
